import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

function PasswordInput({ value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input className="w-full bg-white border border-zinc-300 rounded-sm pl-3 pr-10 py-2.5 text-sm text-zinc-900 focus:outline-none focus:border-[#007AFF] transition-colors"
        type={show ? "text" : "password"} required value={value} onChange={onChange} data-testid="auth-password-input" />
      <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"} data-testid="toggle-password-visibility"
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-500 hover:text-zinc-900 transition-colors">
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

export default function AuthPage({ mode }) {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState("");
  const isLogin = mode === "login";
  const [sys, setSys] = useState(null);
  useEffect(() => { api.get("/system/public").then((r) => setSys(r.data)).catch(() => {}); }, []);
  const closed = !isLogin && sys && !sys.allow_registration;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (isLogin) {
        await login(f.email, f.password);
        nav("/");
      } else {
        const r = await register(f.name, f.email, f.password);
        if (r.pending) setPending(r.message);
        else { toast.success(r.message); nav("/login"); }
      }
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  const inp = "w-full bg-white border border-zinc-300 rounded-sm px-3 py-2.5 text-sm text-zinc-900 focus:outline-none focus:border-[#007AFF] transition-colors";
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.2fr_1fr] bg-white">
      <div className="relative hidden lg:block overflow-hidden bg-zinc-900">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900/20 via-zinc-900/50 to-zinc-900/90" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 bg-[#007AFF] grid place-items-center rounded-sm"><span className="font-display font-black text-[11px]">FX</span></div>
            <span className="font-display font-black text-lg">Fx Cotrader</span>
          </div>
          <div className="max-w-lg fade-up">
            <p className="label-xs !text-[#A78BFA] mb-4">AI Forex Decision Support</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02]">Read the market.<br />Weigh the odds.<br /><span className="text-[#4DA3FF]">Trade with a plan.</span></h1>
            <p className="mt-6 text-zinc-300 text-base">Multi-timeframe confirmation, price-action structure, regime detection and an ML probability layer — distilled into scored, explainable setups.</p>
          </div>
          <p className="text-[11px] text-zinc-400 max-w-md">Probabilistic analysis only. No outcome is guaranteed. Not financial advice.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        {closed ? (
          <div className="w-full max-w-sm space-y-4 fade-up" data-testid="registration-closed">
            <h2 className="text-2xl font-black text-zinc-900">Registrations closed</h2>
            <p className="text-sm text-zinc-600">New sign-ups are paused right now.{sys.support_email && ` Contact ${sys.support_email} for access.`}</p>
            <Link to="/login" className="inline-block text-sm text-[#007AFF] hover:underline">Back to sign in</Link>
          </div>
        ) : pending ? (
          <div className="w-full max-w-sm space-y-4 fade-up" data-testid="registration-pending">
            <div className="h-10 w-10 rounded-full bg-[#D97706]/10 grid place-items-center"><Clock size={18} className="text-[#D97706]" /></div>
            <h2 className="text-2xl font-black text-zinc-900">Awaiting approval</h2>
            <p className="text-sm text-zinc-600">{pending}</p>
            <Link to="/login" onClick={() => setPending("")} className="inline-block text-sm text-[#007AFF] hover:underline" data-testid="pending-back-to-login">Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm space-y-5 fade-up" data-testid={`${mode}-form`}>
            <div>
              <p className="label-xs mb-2">{isLogin ? "Welcome back" : "Create account"}</p>
              <h2 className="text-3xl font-black text-zinc-900">{isLogin ? "Sign in" : "Join Fx Cotrader"}</h2>
              {!isLogin && <p className="text-xs text-zinc-500 mt-2">New accounts are reviewed and approved by an administrator.</p>}
            </div>
            {!isLogin && (
              <label className="block space-y-1.5"><span className="label-xs">Name</span>
                <input className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="auth-name-input" /></label>
            )}
            <label className="block space-y-1.5"><span className="label-xs">Email</span>
              <input className={inp} type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="auth-email-input" /></label>
            <div className="space-y-1.5"><span className="label-xs">Password</span>
              <PasswordInput value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
            {err && <p className="text-sm text-[#DC2626]" data-testid="auth-error">{err}</p>}
            <button disabled={busy} className="w-full py-2.5 bg-[#007AFF] hover:bg-[#1a86ff] active:scale-[0.99] transition-[background-color,transform] rounded-sm text-sm font-semibold text-white disabled:opacity-50" data-testid="auth-submit-btn">
              {busy ? "Please wait…" : isLogin ? "Sign in" : "Request account"}
            </button>
            <p className="text-sm text-zinc-500">
              {isLogin ? "No account? " : "Already registered? "}
              <Link to={isLogin ? "/register" : "/login"} className="text-[#007AFF] hover:underline" data-testid="auth-switch-link">{isLogin ? "Create one" : "Sign in"}</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
